// ── Manufacturing Order (Main PO) — print document, MOZI template (mozPrint.js) ──
import {
  esc, has, fmtDate, fmtNum, thumbCell, heroHtml, tabHtml, detailsHtml, signoffHtml, buildMozDocument, brandWordOf,
} from './mozPrint';
import { QTY_FINISHES, finishQtyOf, restQtyOf } from '../../utils/finishQty';

const STATUS_LABEL = { draft: 'Draft', submitted: 'Pending Approval', approved: 'Approved', rejected: 'Rejected' };

export function buildMainPoHtml(po, settings = {}, origin = window.location.origin) {
  const status = po.status || 'draft';
  const items = po.items || [];

  // Order details (real fields only)
  const details = [
    ['Manufacturing Order No.', po.po_no, ' big'],
    ['Order Date', fmtDate(po.order_date)],
    ['Client / Project', [po.client_name, po.project_name].filter(has).join(' / ')],
    ['Timelines', po.timelines],
    ['PMC Name', po.pmc_name],
    ['Project Code', po.linked_project_code],
    ['Production Incharge', ''],
    ['Prepared By', po.created_by_name],
  ];

  const rows = items.map((it, i) => {
    const dims = (it.size_length || it.size_width || it.size_thickness)
      ? `L ${fmtNum(it.size_length) || '—'} × W ${fmtNum(it.size_width) || '—'} × T ${fmtNum(it.size_thickness) || '—'} mm` : '';
    // Paint / Fabric show their own quantity; "without" when only part of the Qty needs it
    const finishes = [
      ...QTY_FINISHES.filter(f => it[f.flag]).map(f => {
        const rest = restQtyOf(it, f);
        return `${f.short}: ${fmtNum(finishQtyOf(it, f))}${rest > 0 ? ` <small>(${fmtNum(rest)} without)</small>` : ''}`;
      }),
      it.is_metal_finish && 'Metal',
    ].filter(Boolean);
    return `<tr>
      <td class="c sn">${i + 1}</td>
      <td class="pc">${esc(it.proto_code || '')}</td>
      <td class="pn">
        <div class="pname">${esc(it.product_name)}</div>
        ${has(it.area) ? `<div class="psub">Area: ${esc(it.area)}</div>` : ''}
        ${has(it.product_size) ? `<div class="psub">Size: ${esc(it.product_size)}</div>` : ''}
        ${dims ? `<div class="psub">${esc(dims)}</div>` : ''}
      </td>
      <td class="im">${thumbCell(it.images)}</td>
      <td class="fn">
        ${finishes.length ? `<div class="chips">${finishes.map(f => `<span class="chip">${f}</span>`).join('')}</div>` : ''}
        ${has(it.finishes_note) ? `<div class="fnote">${esc(it.finishes_note)}</div>` : ''}
      </td>
      <td class="c qty">${esc(fmtNum(it.qty))}</td>
      <td class="rm">${esc(it.remark || '')}</td>
    </tr>`;
  }).join('');
  const totalQty = items.reduce((s, it) => s + (Number(it.qty) || 0), 0);
  const finishTotals = QTY_FINISHES.map(f => [f.short, items.reduce((s, it) => s + finishQtyOf(it, f), 0)])
    .filter(([, q]) => q > 0).map(([l, q]) => `${l} Qty: ${fmtNum(q)}`).join(' &nbsp;·&nbsp; ');

  const body = `
  ${heroHtml({ brandWord: brandWordOf(settings), titleTop: 'Manufacturing', titleAccent: 'Order', sub: STATUS_LABEL[status] || status })}

  ${tabHtml('doc', 'Order Details', '58mm')}
  ${detailsHtml(details)}

  ${tabHtml('box', 'Product List &amp; Manufacturing Details', '118mm')}
  <table class="items">
    <thead><tr>
      <th class="c sn">S.No.</th><th>Proto Code</th><th>Product Name &amp; Description</th><th class="c">Image (Reference)</th>
      <th>Finish</th><th class="c">Qty.</th><th>Remarks</th>
    </tr></thead>
    <tbody>${rows || '<tr><td colspan="7" class="none">No items on this Manufacturing Order</td></tr>'}</tbody>
    ${items.length ? `<tfoot><tr><td colspan="5" class="tl">Total Items: ${items.length}${finishTotals ? ` &nbsp;·&nbsp; ${finishTotals}` : ''}</td><td class="c qty">${esc(fmtNum(totalQty))}</td><td></td></tr></tfoot>` : ''}
  </table>

  ${signoffHtml([['Prepared By', po.created_by_name], ['Production Head', ''], ['QC Approval', '']], po.remarks)}`;

  return buildMozDocument({ docTitle: `${po.po_no || 'Manufacturing Order'} — Manufacturing Order`, body, settings, origin });
}
